import { useEffect, useState } from "react";
import { Link2, RefreshCw, AlertCircle, CheckCircle2, Unplug } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  disconnectFinch,
  exchangeFinchToken,
  getFinchConnectToken,
  getFinchStatus,
  syncFinch,
  type FinchStatus,
  type FinchSyncSummary,
} from "@/api/payrollFinch";

/**
 * SUPER_ADMIN-only payroll integration panel.
 *
 * Flow:
 *   1. Click "Connect" → POST /connect-token → open `connect_url` in a new tab.
 *   2. User picks a payroll provider in Finch Connect; provider redirects
 *      back to FINCH_REDIRECT_URI with `?code=public_xyz`.
 *   3. Operator pastes that public_token here (or the redirect handler does
 *      it programmatically) → POST /exchange persists the access_token.
 *   4. "Sync now" pulls the directory + employment and shows a diff summary.
 *
 * Sync diff is informational — we never auto-mutate Employee rows from
 * Finch. The operator decides what to do with the unmatched/missing buckets.
 */
export function PayrollIntegrationSettings() {
  const { addToast } = useToast();
  const [status, setStatus] = useState<FinchStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState("");
  const [diff, setDiff] = useState<FinchSyncSummary | null>(null);

  const refresh = async () => {
    try {
      setStatus(await getFinchStatus());
    } catch {
      addToast("error", "Failed to load Finch status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onConnect = async () => {
    setBusy("connect");
    try {
      const token = await getFinchConnectToken();
      window.open(token.connect_url, "_blank", "noopener,noreferrer");
      addToast(
        "info",
        "Finch Connect opened in a new tab. Paste the returned token below to finish.",
      );
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { detail?: string } } };
      if (ax.response?.status === 503) {
        addToast(
          "error",
          "Finch credentials are not configured on the server (FINCH_CLIENT_ID).",
        );
      } else {
        addToast("error", ax.response?.data?.detail || "Failed to start Finch Connect");
      }
    } finally {
      setBusy(null);
    }
  };

  const onExchange = async () => {
    if (!publicToken.trim()) return;
    setBusy("exchange");
    try {
      await exchangeFinchToken(publicToken.trim());
      setPublicToken("");
      addToast("success", "Connected to Finch");
      await refresh();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Exchange failed");
    } finally {
      setBusy(null);
    }
  };

  const onSync = async () => {
    setBusy("sync");
    try {
      const summary = await syncFinch();
      setDiff(summary);
      addToast(
        "success",
        `Pulled ${summary.pulled} · matched ${summary.matched} · ${summary.unmatched} new`,
      );
      await refresh();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(null);
    }
  };

  const onDisconnect = async () => {
    if (!confirm("Disconnect Finch? Your access_token will be revoked.")) return;
    setBusy("disconnect");
    try {
      await disconnectFinch();
      setDiff(null);
      addToast("success", "Disconnected");
      await refresh();
    } catch {
      addToast("error", "Disconnect failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <Card><p className="text-sm text-text-muted">Loading…</p></Card>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Finch payroll integration
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted mb-4">
          Finch is a unified API that connects to Gusto, Rippling, ADP Run,
          Paychex, Justworks and 200+ other payroll providers. One connection
          here covers any provider your customer uses.
        </p>

        <div className="rounded-md border border-border/50 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {status?.connected ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-text-muted" />
              )}
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {status?.connected ? "Connected" : "Not connected"}
                </p>
                {status?.connected && (
                  <p className="text-xs text-text-muted">
                    Provider:{" "}
                    <span className="font-mono">{status.provider_id || "unknown"}</span>
                    {status.last_synced_at && (
                      <>
                        {" · last synced "}
                        {new Date(status.last_synced_at).toLocaleString()}
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
            {status?.connected ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onSync}
                  isLoading={busy === "sync"}
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Sync now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onDisconnect}
                  isLoading={busy === "disconnect"}
                >
                  <Unplug className="h-3 w-3 mr-1" /> Disconnect
                </Button>
              </div>
            ) : (
              <Button onClick={onConnect} isLoading={busy === "connect"}>
                Connect via Finch
              </Button>
            )}
          </div>

          {status?.connected && (status.products?.length ?? 0) > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {status.products.map((p) => (
                <Badge key={p}>{p}</Badge>
              ))}
            </div>
          )}

          {status?.last_error && (
            <p className="mt-3 text-xs text-danger">Last error: {status.last_error}</p>
          )}
        </div>

        {!status?.connected && (
          <div className="rounded-md border border-dashed border-border/60 p-4">
            <p className="text-xs font-medium text-text-primary mb-2">
              Already started Finch Connect? Paste the returned public_token here:
            </p>
            <div className="flex gap-2">
              <Input
                value={publicToken}
                onChange={(e) => setPublicToken(e.target.value)}
                placeholder="public_xxx"
              />
              <Button
                onClick={onExchange}
                isLoading={busy === "exchange"}
                disabled={!publicToken.trim()}
              >
                Exchange
              </Button>
            </div>
          </div>
        )}
      </Card>

      {diff && (
        <Card>
          <CardHeader>
            <CardTitle>Last sync diff</CardTitle>
          </CardHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat label="Pulled" value={diff.pulled} />
            <Stat label="Matched" value={diff.matched} />
            <Stat label="New in Finch" value={diff.unmatched} tone="warning" />
            <Stat label="Missing in Finch" value={diff.missing} tone="muted" />
          </div>
          {diff.unmatched_individuals.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-text-primary mb-2">
                People in Finch but not in Lenavio — candidates to import
              </p>
              <DiffList
                items={diff.unmatched_individuals.map((i) => ({
                  key: i.finch_id,
                  primary: `${i.first_name ?? ""} ${i.last_name ?? ""}`.trim() || "(no name)",
                  secondary: [i.email, i.title, i.department].filter(Boolean).join(" · "),
                }))}
              />
            </div>
          )}
          {diff.missing_employees.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-primary mb-2">
                People in Lenavio but not in Finch
              </p>
              <DiffList
                items={diff.missing_employees.map((m) => ({
                  key: m.employee_id,
                  primary: m.email ?? m.employee_id,
                  secondary: "May be terminated or not yet onboarded in payroll",
                }))}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "warning" | "muted";
}) {
  const colour =
    tone === "warning"
      ? "text-warning"
      : tone === "muted"
      ? "text-text-muted"
      : "text-accent";
  return (
    <div className="rounded-md border border-border/50 p-3">
      <p className="text-xs text-text-muted">{label}</p>
      <p className={`text-2xl font-semibold ${colour}`}>{value}</p>
    </div>
  );
}

function DiffList({
  items,
}: {
  items: Array<{ key: string; primary: string; secondary: string }>;
}) {
  return (
    <ul className="max-h-60 overflow-y-auto rounded border border-border/50 divide-y divide-border/50 text-xs">
      {items.slice(0, 100).map((it) => (
        <li key={it.key} className="px-3 py-2">
          <p className="font-medium text-text-primary">{it.primary}</p>
          <p className="text-text-muted">{it.secondary}</p>
        </li>
      ))}
    </ul>
  );
}
